import React, { useCallback, useEffect, useState } from 'react'
import * as yup from 'yup'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { FastField, Formik, Form } from 'formik'
import isEmpty from 'lodash/isEmpty'
import { event as gaEvent } from 'react-ga'

import { Button } from 'pharmacy/src/input/button'
import { ProximaNova } from 'pharmacy/src/typography'
import { TextInput } from 'pharmacy/src/input/textInput'
import { Link } from 'pharmacy/src/navigation/link'

import { mixpanel } from 'mednet-util/src/tracking'

import { login } from 'mednet-cns/src/reducers/user'
import { useGregorFlagsReady } from 'mednet-cns/src/hooks/gregor'

import { TitledSection } from 'components/titledSection'

import useHomePath from '../../hooks/useHomePath'

import css from './loginForm.scss'

const loginSchema = yup.object().shape({
  // We are adding strip to the end of the email-schema to ignore accidental spaces
  // doc: https://github.com/jquense/yup#schemastripenabled-boolean--true-schema
  username: yup.string().required('Required').email('Invalid email').trim(),
  password: yup.string().required('Required'),
})

const getValidationTag = (fieldName, touched, errors) =>
  touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined

const TextInputField = ({ name, touched, errors, ...restProps }) => {
  const validationError = getValidationTag(name, touched, errors)

  return (
    <FastField name={name}>
      {({ field }) => (
        <TextInput
          className={classNames(css.loginFormField, {
            [css.invalidLoginFormField]: validationError,
          })}
          {...field}
          {...restProps}
          typeStyle="proximaNovaPrimary"
          showClear={false}
          onChangeHandlesEvent
          validationTag={validationError}
        />
      )}
    </FastField>
  )
}

const handleLoginFormSubmission = (
  values,
  dispatch,
  setSubmitting,
  setLoginStatus,
  setErrors
) => {
  dispatch(
    login(
      { ...values, distinct_id: mixpanel.get_distinct_id() },
      (response) => {
        if (response.errors) {
          setSubmitting(false)
          setErrors({
            [Object.keys(response.errors)[0]]: Object.values(
              response.errors
            )[0],
          })
        } else {
          setLoginStatus(true)
          setSubmitting(false)
        }
      }
    )
  )
}

const InterenalLoginForm = (props) => {
  const {
    isSubmitting,
    touched,
    errors,
    handleSubmit,
    values,
    setErrors,
    history,
  } = props

  const handleRegisterClick = useCallback(
    (e) => {
      e.preventDefault()

      // Preserve existing query params (from, invitecode, etc.)
      const params = new URLSearchParams(window.location.search)

      // Add/override with new params
      params.set('src', 'login')
      params.set('src_path', history?.location?.pathname || '')
      params.set('src_item', 'login')

      // Always go to /register - PHP will redirect to V2 if needed
      window.location.href = `/register?${params}`
    },
    [history]
  )

  const trackValidationFailure = useCallback(() => {
    if (!isEmpty(errors)) {
      mixpanel.track('Login Attempt - JS Validation Failure', {
        loginEmail: values.username,
        errors,
      })
    }
  }, [errors, values.username])

  useEffect(trackValidationFailure, [!isEmpty(errors) && errors])

  useEffect(() => {
    setErrors({})
  }, [values])

  return (
    <Form>
      <TextInputField
        name="username"
        placeholder="Email"
        touched={touched}
        errors={errors}
      />

      <TextInputField
        name="password"
        placeholder="Password"
        touched={touched}
        errors={errors}
        isPassword
      />

      <Button
        type="orange"
        className={css.loginFormButton}
        onClick={handleSubmit}
        isLoading={isSubmitting}
        submitButton
      >
        <ProximaNova.Text2>Sign In</ProximaNova.Text2>
      </Button>

      <Link
        className={classNames(
          css.loginFormHelperQuestion,
          css.forgotPasswordLink
        )}
        pathname="/recoverpassword"
      >
        Forgot password?
      </Link>

      <hr className={css.loginFormHorizontalLine} />

      <div className={css.loginFormRegisterOptionContainer}>
        <div className={css.loginFormHelperQuestion}>
          Don&rsquo;t have an account?
          <Button
            type="dark_blue"
            size="small"
            onClick={handleRegisterClick}
            className={css.loginFormRegisterButton}
          >
            <div className={css.loginFormRegisterButtonText}>Register</div>
          </Button>
        </div>
      </div>
    </Form>
  )
}

export const LoginForm = ({ history }) => {
  const [loggedInSuccessfully, setLoginStatus] = useState(false)

  const dispatch = useDispatch()
  const { homePath } = useHomePath()
  const gregorReady = useGregorFlagsReady()

  const initialLoginFormValues = {
    username: undefined,
    password: undefined,
  }

  useEffect(() => {
    // Redirect once login succeeds. The new user's flags aren't loaded here, so
    // homePath is the default and the server bounces v2-feed users to /v2/feed.
    if (loggedInSuccessfully && gregorReady && typeof window !== 'undefined') {
      if (process.env.GOOGLE_ANALYTICS_ID) {
        gaEvent({
          category: 'SignIn',
          action: 'CompletedSignIn',
        })
      }

      const fromRedirectParam = new URLSearchParams(window.location.search).get(
        'from'
      )
      const redirectUrl = fromRedirectParam ?? homePath

      // Use window.location.href instead of React Router's <Redirect> to force a full page reload.
      // This is necessary because many redirect URLs are PHP routes that require server-side processing:
      // - PHP routes need to execute on the server (e.g., /question/publicView/123 → /question/view/123)
      // - Session cookies must be sent with the request for authentication
      // - Server-side 302 redirects need to be triggered
      // React Router's <Redirect> only handles client-side navigation and cannot trigger server-side PHP execution.
      // The <Redirect> fallback below is only for SSR scenarios (which shouldn't occur since login is client-side only).
      if (typeof window !== 'undefined') {
        window.location.href = redirectUrl
      }
    }
  }, [homePath, loggedInSuccessfully, gregorReady])

  return (
    <div className={css.loginFormContainer}>
      <TitledSection header3="Sign In" headerLeftAligned>
        <Formik
          initialValues={initialLoginFormValues}
          validationSchema={loginSchema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values, { setSubmitting, setErrors }) => {
            handleLoginFormSubmission(
              values,
              dispatch,
              setSubmitting,
              setLoginStatus,
              setErrors
            )
          }}
        >
          {(props) => <InterenalLoginForm {...props} history={history} />}
        </Formik>
      </TitledSection>
    </div>
  )
}
